import { useCallback } from "react";
import {
  acquireBackendTokenSilent,
  authStorage,
} from "components/Intune/AuthService";
import { MSAL_SCOPES } from "components/Intune/IntuneScopes";
import { getIntuneMsalInstance } from "components/Intune/intuneMsal";

// getIntuneMsalInstance() returns a stable module-level singleton (until an explicit
// logout resets it), so memoizing on it keeps these callbacks referentially stable across
// renders. Without that, every render would hand out new functions, and effects depending
// on them (see IntuneToggle) would re-run every render instead of once per mount.
export const useAuth = () => {
  const instance = getIntuneMsalInstance();

  const loginToTenant = useCallback(async (tenantId: string) => {
    try {
      await instance.loginRedirect({
        authority: `https://login.microsoftonline.com/${tenantId}`,
        scopes: MSAL_SCOPES,
        prompt: "select_account",
        state: JSON.stringify({ tenantId }),
      });
    } catch (error) {
      console.error("Login failed:", error);
    }
  }, [instance]);

  const getToken = useCallback(async () => {
    return await acquireBackendTokenSilent(instance);
  }, [instance]);

  const logoutIntune = useCallback(() => {
    authStorage.clear();
  }, []);

  const handleRedirect = useCallback(async () => {
    try {
      const response = await instance.handleRedirectPromise();

      if (response?.accessToken && response.account) {
        let tenantId = null;

        if (response.state) {
          try {
            const parsed = JSON.parse(response.state);
            tenantId = parsed.tenantId;
          } catch {}
        }

        if (tenantId) {
          authStorage.setTenantId(tenantId);
        }
        authStorage.setHomeAccountId(response.account.homeAccountId);
        return true;
      }
    } catch (err) {
      // Ok to ignore errors here, as they may occur if the user cancels the login or if there's a redirect without a valid response.
    }

    return false;
  }, [instance]);

  const tryAutoLogin = useCallback(async () => {
    const tenantId = authStorage.getTenantId();
    const homeAccountId = authStorage.getHomeAccountId();

    // If session not available → do nothing
    if (!tenantId || !homeAccountId) return false;

    const accounts = instance.getAllAccounts();

    const account = accounts.find(
      acc => acc.homeAccountId === homeAccountId && acc.tenantId === tenantId
    );

    // Step 1: Try silent login if account exists
    if (account) {
      try {
        await instance.acquireTokenSilent({
          account,
          authority: `https://login.microsoftonline.com/${tenantId}`,
          scopes: MSAL_SCOPES,
        });

        return true;
      } catch (err) {
        console.warn("Silent token failed → fallback to redirect");
      }
    }

    // Step 2: fallback to redirect login
    try {
      await instance.loginRedirect({
        authority: `https://login.microsoftonline.com/${tenantId}`,
        scopes: MSAL_SCOPES,
        prompt: "none",
        state: JSON.stringify({ tenantId }),
      });
    } catch (err) {
      console.error("Auto login failed:", err);
    }

    return false;
  }, [instance]);

  return {
    getToken,
    loginToTenant,
    logoutIntune,
    tryAutoLogin,
    handleRedirect,
  };
};